import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useConversationStore, useConversationActions } from '../stores/useConversationStore';
import { useTenantStore } from '../stores/useTenantStore';
import '../styles/conversations.css';

const Sidebar: React.FC = () => {
  const auth = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const profile = auth.user?.profile;
  const displayName = profile?.name || profile?.email || 'User';
  const displayEmail = (profile?.email as string) || '';

  // Store des conversations
  const {
    conversations,
    currentConversationId,
    conversationsLoading,
    conversationsError
  } = useConversationStore();
  const { selectConversation, deleteConversation } = useConversationActions();

  const handleLogout = async () => {
    await auth.removeUser();
    useTenantStore.getState().reset();
  };

  // Fermer le menu si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{
      width: '280px',
      height: '100vh',
      backgroundColor: 'white',
      color: '#333',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid #e1e5e9'
    }}>
      {/* Header avec logo/titre */}
      <div style={{
        padding: '1.5rem',
        borderBottom: '1px solid #e1e5e9'
      }}>
        <h1 style={{
          margin: 0,
          fontSize: '1.5rem',
          fontWeight: 'bold'
        }}>
          <span style={{ color: '#333' }}>Nautailus</span>{' '}
          <span style={{ color: '#E85D04' }}>Chat</span>
        </h1>
      </div>

      {/* Zone principale du sidebar - Conversations */}
      <div style={{
        flex: 1,
        padding: '1rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header conversations */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333'
          }}>
            Conversations
          </h2>
          <button
            onClick={() => {
              // Fonction pour démarrer une nouvelle conversation
              useConversationStore.setState({
                currentConversationId: null,
                currentMessages: []
              });
            }}
            style={{
              padding: '0.5rem',
              backgroundColor: '#E85D04',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d14d03';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#E85D04';
            }}
            title="Nouvelle conversation"
          >
            + New
          </button>
        </div>

        {/* Liste des conversations */}
        <div className="conversations-list" style={{
          flex: 1,
          overflow: 'auto'
        }}>
          {conversationsLoading ? (
            <div style={{
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.875rem',
              padding: '1rem'
            }}>
              Loading conversations...
            </div>
          ) : conversationsError ? (
            <div style={{
              textAlign: 'center',
              color: '#dc2626',
              fontSize: '0.875rem',
              padding: '1rem'
            }}>
              Failed to load conversations
            </div>
          ) : conversations.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.875rem',
              padding: '1rem'
            }}>
              No conversations yet.<br />Start chatting to create one!
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation.id}
                className="conversation-item"
                onClick={() => selectConversation(conversation.id)}
                style={{
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  borderRadius: '8px',
                  backgroundColor: conversation.id === currentConversationId ? '#f3f4f6' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  border: conversation.id === currentConversationId ? '1px solid #E85D04' : '1px solid transparent',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (conversation.id !== currentConversationId) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (conversation.id !== currentConversationId) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '4px',
                  lineHeight: '1.4',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {conversation.title}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>{conversation.model_name}</span>
                  <span>{new Date(conversation.updated_at).toLocaleDateString()}</span>
                </div>

                {/* Bouton supprimer (visible au hover) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete conversation "${conversation.title}"?`)) {
                      deleteConversation(conversation.id);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(220, 38, 38, 0.1)',
                    color: '#dc2626',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                  }}
                  className="delete-btn"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Menu utilisateur en bas */}
      <div style={{
        borderTop: '1px solid #e1e5e9',
        padding: '1rem',
        position: 'relative'
      }} ref={userMenuRef}>
        <button
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#333',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            fontSize: '0.875rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            {/* Avatar circle */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#E85D04',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              {displayName[0].toUpperCase()}
            </div>
            <div style={{
              textAlign: 'left',
              overflow: 'hidden'
            }}>
              <div style={{
                fontWeight: '500',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {displayName}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {displayEmail}
              </div>
            </div>
          </div>

          {/* Icône dropdown */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          >
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>

        {/* Dropdown menu */}
        {isUserMenuOpen && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '1rem',
            right: '1rem',
            marginBottom: '0.5rem',
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e1e5e9',
            overflow: 'hidden'
          }}>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#dc2626',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16,17 21,12 16,7"/>
                <line x1="21" x2="9" y1="12" y2="12"/>
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;